/** Calendar day at local midnight (avoids timezone drift in comparisons). */
export function stripTime(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** First day of the editable window: yesterday (today and yesterday are editable). */
export function getTimesheetEditableCutoff(windowDays = 2, baseDate = new Date()): Date {
  const today = stripTime(baseDate);
  today.setDate(today.getDate() - Math.max(windowDays - 1, 0));
  return today;
}

function getTimesheetWindowStart(date: Date): Date {
  const start = stripTime(date);
  start.setHours(12, 0, 0, 0);
  return start;
}

function getTimesheetWindowEnd(date: Date, windowDays: number): Date {
  const end = stripTime(date);
  end.setDate(end.getDate() + Math.max(windowDays - 1, 0));
  end.setHours(23, 59, 59, 999);
  return end;
}

export interface TimesheetViewOnlyOptions {
  allowedTill?: string | null;
  timesheetFound?: boolean;
  editableWindowDays?: number;
  now?: Date;
}

export type TimesheetLockReason = "VIEW_ONLY" | "TODAY_BEFORE_NOON" | null;

/** Parses API `allowedTill` (e.g. `2026-04-29T00:00:00`) to a local calendar date. */
export function parseAllowedTillDate(
  value: string | null | undefined,
): Date | null {
  if (value == null || String(value).trim() === "") return null;

  const datePart = String(value).split("T")[0];
  const parts = datePart.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;

  const [year, month, day] = parts;
  const parsed = stripTime(new Date(year, month - 1, day));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * True when the selected work day is outside the editable window.
 * Starts on the selected day at noon. The API's `allowedTill` includes the
 * next working day (skipping weekends and approved holidays) or an extension.
 * Without API metadata, fall back to the calendar-day window.
 */
export function isTimesheetHistoricalViewOnly(
  date: Date,
  options?: TimesheetViewOnlyOptions,
): boolean {
  return getTimesheetLockReason(date, options) != null;
}

export function getTimesheetLockReason(
  date: Date,
  options?: TimesheetViewOnlyOptions,
): TimesheetLockReason {
  const selected = stripTime(date);
  const now = options?.now ?? new Date();
  const today = stripTime(now);
  const editableWindowDays = options?.editableWindowDays ?? 2;
  const windowStart = getTimesheetWindowStart(selected);
  const windowEnd = getTimesheetWindowEnd(selected, editableWindowDays);

  // Rule 0: today's timesheet opens at 12:00 PM.
  if (selected.getTime() === today.getTime() && now.getTime() < windowStart.getTime()) {
    return "TODAY_BEFORE_NOON";
  }

  // Rule 1: local fallback window; remote users keep their wider window.
  if (
    now.getTime() >= windowStart.getTime() &&
    now.getTime() <= windowEnd.getTime()
  ) {
    return null;
  }

  // Rule 2: server deadline includes the next working day or an extension.
  const allowedTill = parseAllowedTillDate(options?.allowedTill);
  if (allowedTill && today.getTime() <= allowedTill.getTime()) {
    return null;
  }

  // Otherwise locked (show "Timesheet window is closed")
  return "VIEW_ONLY";
}
